import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Search, Loader2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Client, Supplier } from '../../types';
import { supabase } from '../../lib/supabase';
import { useAppStore } from '../../store';
import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOnOpen } from '../../hooks/useOnOpen';
import ErrorModal from './ErrorModal';

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'client' | 'supplier';
  onSave: (entity: Client | Supplier) => void;
  initialData?: Client | Supplier;
}

const taxConditionOptions = [
  { value: '', label: 'Seleccionar' },
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributista' },
  { value: 'EX', label: 'Exento' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'NC', label: 'No Categorizado' },
];

const EntityModal: React.FC<EntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  onSave,
  initialData,
}) => {
  const { setError, salespersons, currentUser } = useAppStore();
  const { userRole } = useAuth();

  const isVendedorCreatingClient = useMemo(
    () =>
      entityType === 'client' &&
      !initialData &&
      userRole?.trim().toLowerCase() === 'vendedor',
    [entityType, initialData, userRole]
  );
  const [formData, setFormData] = useState({
    cuit: '',
    business_name: '',
    name: '',
    tax_condition: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    category: '',
    salesperson: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [supplierCategories, setSupplierCategories] = useState<{id: string, name: string}[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const salespersonOptions = useMemo(() => {
    const opts = salespersons.map(sp => ({ value: sp.name, label: sp.name }));
    if (isVendedorCreatingClient) {
      return [{ value: '', label: 'Seleccionar vendedor...' }, ...opts];
    }
    return [{ value: '', label: 'Ninguno' }, ...opts];
  }, [salespersons, isVendedorCreatingClient]);

  const fetchSupplierCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const { data } = await supabase.from('supplier_categories').select('id, name').order('name');
      let cats = data || [];
      if (formData.category && !cats.some(c => c.name === formData.category)) {
        cats = [...cats, { id: 'custom', name: formData.category }];
      }
      setSupplierCategories(cats);
    } catch {
      setLocalError('Error al cargar categorías');
      setShowErrorModal(true);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useOnOpen(isOpen, () => {
    if (entityType === 'supplier') fetchSupplierCategories();
    setShowAdvanced(false);
    if (initialData) {
      const e = initialData as Client | Supplier;
      setFormData({
        cuit: e.cuit || '',
        business_name: e.business_name || '',
        name: e.name,
        tax_condition: e.tax_condition || '',
        email: e.email || '',
        phone: e.phone || '',
        address: e.address || '',
        city: e.city || '',
        postal_code: e.postal_code || '',
        province: e.province || '',
        category: (e as Supplier).category || '',
        salesperson: (e as Client).salesperson || '',
      });
    } else {
      let defaultSalesperson = '';
      if (
        entityType === 'client' &&
        userRole?.trim().toLowerCase() === 'vendedor' &&
        currentUser?.email
      ) {
        const sp = salespersons.find(
          s => s.email.toLowerCase() === currentUser.email!.toLowerCase()
        );
        if (sp) defaultSalesperson = sp.name;
      }
      setFormData({
        cuit: '', business_name: '', name: '', tax_condition: '',
        email: '', phone: '', address: '', city: '', postal_code: '', province: '',
        category: '', salesperson: defaultSalesperson,
      });
    }
    setLocalError(null);
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setLocalError(null);
  };

  const fetchAfipData = async () => {
    if (!formData.cuit) {
      setLocalError('Ingresá un CUIT');
      setShowErrorModal(true);
      return;
    }
    setIsLoading(true);
    setLocalError(null);
    try {
      const res = await fetch(
        `${supabase.supabaseUrl}/functions/v1/afip-padron?cuit=${formData.cuit}`,
        { headers: { Authorization: `Bearer ${supabase.supabaseKey}` } }
      );
      if (!res.ok) throw new Error('Error al conectar con AFIP');
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al consultar AFIP');
      setFormData(prev => ({
        ...prev,
        business_name: data.data.nombre,
        name: data.data.nombre,
        address: data.data.domicilioFiscal.direccion,
        city: data.data.domicilioFiscal.localidad,
        postal_code: data.data.domicilioFiscal.codPostal,
        province: data.data.domicilioFiscal.descripcionProvincia,
        tax_condition: data.data.tipoContribuyente === 'RESPONSABLE INSCRIPTO' ? 'RI' : 'NC',
      }));
    } catch (err: any) {
      setLocalError(err.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setLocalError('El nombre no puede estar vacío');
      setShowErrorModal(true);
      return;
    }
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('supplier_categories')
        .insert([{ name: newCategoryName.trim() }])
        .select()
        .single();
      if (error) throw error.code === '23505' ? new Error('Ya existe esa categoría') : error;
      setSupplierCategories(prev => [...prev, data]);
      setFormData(prev => ({ ...prev, category: data.name }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err: any) {
      setLocalError(err.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setLocalError('El nombre es obligatorio');
      setShowErrorModal(true);
      return;
    }
    if (entityType === 'client' && !initialData) {
      if (!formData.email?.trim()) {
        setLocalError('El email es obligatorio');
        setShowErrorModal(true);
        return;
      }
      if (!formData.phone?.trim()) {
        setLocalError('El teléfono es obligatorio');
        setShowErrorModal(true);
        return;
      }
    }
    if (entityType === 'supplier' && !formData.category.trim()) {
      setLocalError('Seleccioná una categoría');
      setShowErrorModal(true);
      return;
    }
    if (isVendedorCreatingClient && !formData.salesperson?.trim()) {
      setLocalError('El vendedor es obligatorio');
      setShowErrorModal(true);
      return;
    }
    try {
      let entity: any = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        cuit: formData.cuit || null,
        business_name: formData.business_name || null,
        tax_condition: formData.tax_condition || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        province: formData.province || null,
      };
      if (entityType === 'client') entity.salesperson = formData.salesperson || null;
      if (initialData) {
        entity.id = initialData.id;
        if (initialData.balance !== undefined) entity.balance = initialData.balance;
      }
      if (entityType === 'supplier') entity.category = formData.category || null;
      await onSave(entity);
      onClose();
    } catch (err: any) {
      if (err.message?.includes('duplicate')) {
        setLocalError(err.message.includes('_email_key') ? 'Ya existe con ese email' : err.message.includes('_cuit_key') ? 'Ya existe con ese CUIT' : 'Ya existe');
        setShowErrorModal(true);
      } else setError(err.message);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[70]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none z-[70]">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {initialData ? 'Editar' : 'Nuevo'} {entityType === 'client' ? 'cliente' : 'proveedor'}
                  </h2>
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="flex gap-2 mb-4">
                <input
                  id="cuit"
                  name="cuit"
                  value={formData.cuit}
                  onChange={handleInputChange}
                  placeholder="CUIT"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                />
                <Button type="button" variant="outline" onClick={fetchAfipData} disabled={isLoading} className="px-4">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              <div className="space-y-3 mb-4">
                <Input label="Razón social" id="business_name" name="business_name" value={formData.business_name} onChange={handleInputChange} />
                <Input label="Nombre" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                {entityType === 'client' && (
                  <Select
                    label="Vendedor"
                    value={formData.salesperson}
                    onChange={(v) => setFormData(prev => ({ ...prev, salesperson: v }))}
                    options={salespersonOptions}
                    required={isVendedorCreatingClient}
                  />
                )}
                <Select label="Condición IVA" value={formData.tax_condition} onChange={(v) => setFormData(prev => ({ ...prev, tax_condition: v }))} options={taxConditionOptions} />
                {entityType === 'supplier' && (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Select
                        label="Categoría"
                        value={formData.category}
                        onChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                        options={[{ value: '', label: 'Seleccionar' }, ...supplierCategories.map(c => ({ value: c.name, label: c.name }))]}
                      />
                    </div>
                    {isAddingCategory ? (
                      <>
                        <input
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Nueva"
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                        />
                        <Button type="button" variant="outline" size="sm" onClick={handleAddCategory} disabled={isLoading || !newCategoryName.trim()}>
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                        </Button>
                        <button type="button" onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }} className="p-2 text-gray-400 hover:text-gray-600">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <Button type="button" variant="outline" size="sm" onClick={() => setIsAddingCategory(true)} icon={<Plus className="h-4 w-4" />}>
                        +
                      </Button>
                    )}
                  </div>
                )}
                {entityType === 'client' && (
                  <>
                    <Input label="Email" type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required={!initialData} />
                    <Input label="Teléfono" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required={!initialData} />
                  </>
                )}
              </div>

              <div className="mb-4">
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Más opciones
                </button>
                {showAdvanced && (
                  <div className="mt-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                    {entityType === 'supplier' && (
                      <>
                        <Input label="Email" type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} />
                        <Input label="Teléfono" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} />
                      </>
                    )}
                    <Input label="Dirección" id="address" name="address" value={formData.address} onChange={handleInputChange} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input label="Ciudad" id="city" name="city" value={formData.city} onChange={handleInputChange} />
                      <Input label="CP" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleInputChange} />
                    </div>
                    <Input label="Provincia" id="province" name="province" value={formData.province} onChange={handleInputChange} />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={isLoading} className="flex-1">
                  {initialData ? 'Guardar' : 'Crear'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={localError || 'Error'} />
    </>
  );
};

export default EntityModal;
